import { resolveCurrentUid } from '@/utils/auth'
import { oauthCallback } from '@/services/sheets'
import { takeGooglePkceVerifier } from '@/utils/googlePkce'
import { invalidateGoogleConnection } from '../hooks/googleConnectionCache'
import {
  finishOnboardingGoogleSession,
  onboardingGoogleNonce,
  readOnboardingGoogleSession,
} from './googleOAuthHistory'

const pending = new Map<string, Promise<void>>()

/** Onboarding has no dashboard resume action, but needs the same OAuth ownership checks. */
export async function completeOnboardingGoogle(
  path: string,
  code: string | null,
  error: string | null
) {
  const nonce = onboardingGoogleNonce(path)
  const uid = await resolveCurrentUid()
  const session = readOnboardingGoogleSession()
  if (!nonce || !session || session.nonce !== nonce || session.uid !== uid) {
    throw new Error(
      'This Google authorization belongs to a different or expired session. Return to setup and reconnect Google.'
    )
  }
  if (session.outcome === 'connected') return
  if (session.outcome === 'cancelled' && error === 'access_denied') return
  if (session.outcome !== 'pending')
    throw new Error('This Google authorization has ended. Return to setup and reconnect Google.')
  if (error === 'access_denied') {
    finishOnboardingGoogleSession(uid, nonce, 'cancelled')
    return
  }
  if (!code || error) {
    finishOnboardingGoogleSession(uid, nonce, 'failed')
    throw new Error('Google authorization did not finish. Return to setup and try again.')
  }
  const existing = pending.get(nonce)
  if (existing) return existing
  // One use: a remount shares `existing` above, and any later replay finds no
  // verifier. Starts made before PKCE shipped have none either.
  const codeVerifier = takeGooglePkceVerifier(nonce)
  if (!codeVerifier) {
    finishOnboardingGoogleSession(uid, nonce, 'failed')
    throw new Error('This Google authorization has expired. Return to setup and reconnect Google.')
  }
  const work = (async () => {
    try {
      // oauthCallback checks Firebase again before and after obtaining the token,
      // so a switch during token retrieval cannot attach this grant to another uid.
      await oauthCallback(code, codeVerifier, uid)
      if ((await resolveCurrentUid()) !== uid)
        throw new Error('Signed-in user changed. Return to setup.')
      finishOnboardingGoogleSession(uid, nonce, 'connected')
      invalidateGoogleConnection(uid)
    } catch (cause) {
      // A newer launch must not be overwritten by an older callback's rejection.
      if (readOnboardingGoogleSession()?.nonce === nonce)
        finishOnboardingGoogleSession(uid, nonce, 'failed')
      throw cause
    }
  })()
  pending.set(nonce, work)
  try {
    await work
  } finally {
    pending.delete(nonce)
  }
}
